import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Globe, Loader2, X, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

// Common languages at the top, then alphabetical
const LANGUAGE_ORDER = [
  'en', 'es', 'fr', 'de', 'zh', 'ja', 'ko', 'pt', 'it', 'ru', 'ar', 'hi',
  'nl', 'pl', 'tr', 'vi', 'th', 'id', 'ms', 'tl', 'sv', 'da', 'no', 'fi',
  'el', 'cs', 'ro', 'hu', 'uk', 'he', 'bn', 'zh-TW'
];

const LANGUAGES = {
  "en": "English",
  "es": "Spanish",
  "fr": "French",
  "de": "German",
  "it": "Italian",
  "pt": "Portuguese",
  "nl": "Dutch",
  "ru": "Russian",
  "zh": "Chinese (Simplified)",
  "zh-TW": "Chinese (Traditional)",
  "ja": "Japanese",
  "ko": "Korean",
  "ar": "Arabic",
  "hi": "Hindi",
  "bn": "Bengali",
  "vi": "Vietnamese",
  "th": "Thai",
  "tr": "Turkish",
  "pl": "Polish",
  "uk": "Ukrainian",
  "he": "Hebrew",
  "sv": "Swedish",
  "da": "Danish",
  "no": "Norwegian",
  "fi": "Finnish",
  "el": "Greek",
  "cs": "Czech",
  "ro": "Romanian",
  "hu": "Hungarian",
  "id": "Indonesian",
  "ms": "Malay",
  "tl": "Filipino/Tagalog"
};

/**
 * TranslateButton - Shows a translate button that reveals translated text
 * @param {string} text - The text to translate
 * @param {string} className - Additional CSS classes
 */
const TranslateButton = ({ text, className = '' }) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [targetLanguage, setTargetLanguage] = useState(null);

  const handleTranslate = async (langCode) => {
    if (!text || !text.trim()) {
      toast.error('No text to translate');
      return;
    }

    setIsTranslating(true);
    setTargetLanguage(langCode);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/translate`,
        {
          text: text,
          target_language: langCode
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTranslatedText(response.data.translated_text);
    } catch (error) {
      console.error('Translation error:', error);
      toast.error(error.response?.data?.detail || 'Translation failed');
      setTranslatedText(null);
      setTargetLanguage(null);
    } finally {
      setIsTranslating(false);
    }
  };

  const clearTranslation = () => {
    setTranslatedText(null);
    setTargetLanguage(null);
  };

  // If we have a translation, show it with option to close
  if (translatedText) {
    return (
      <div className={`mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-blue-600 flex items-center gap-1">
            <Globe className="w-3 h-3" />
            Translated to {LANGUAGES[targetLanguage]}
          </span>
          <button
            onClick={clearTranslation}
            className="text-blue-400 hover:text-blue-600 p-1"
            title="Close translation"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-gray-800 whitespace-pre-wrap">{translatedText}</p>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`text-gray-500 hover:text-blue-600 h-7 px-2 ${className}`}
          disabled={isTranslating}
        >
          {isTranslating ? (
            <>
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              <span className="text-xs">Translating...</span>
            </>
          ) : (
            <>
              <Globe className="w-3 h-3 mr-1" />
              <span className="text-xs">Translate</span>
              <ChevronDown className="w-3 h-3 ml-1" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
        <DropdownMenuLabel className="text-xs text-gray-500">
          Translate to...
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGE_ORDER.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleTranslate(code)}
            className="cursor-pointer"
          >
            {LANGUAGES[code]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Compact translate icon (no text label)
 */
export const TranslateIcon = ({ text, className = '' }) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [targetLanguage, setTargetLanguage] = useState(null);

  const handleTranslate = async (langCode) => {
    if (!text || !text.trim()) {
      toast.error('No text to translate');
      return;
    }

    setIsTranslating(true);
    setTargetLanguage(langCode);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/translate`,
        {
          text: text,
          target_language: langCode
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTranslatedText(response.data.translated_text);
      setShowResult(true);
    } catch (error) {
      console.error('Translation error:', error);
      toast.error(error.response?.data?.detail || 'Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  if (showResult && translatedText) {
    return (
      <div className={`mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-blue-600 flex items-center gap-1">
            <Globe className="w-3 h-3" />
            {LANGUAGES[targetLanguage]}
          </span>
          <button
            onClick={() => {
              setShowResult(false);
              setTranslatedText(null);
            }}
            className="text-blue-400 hover:text-blue-600 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-gray-800 whitespace-pre-wrap text-sm">{translatedText}</p>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600 ${className}`}
          disabled={isTranslating}
          title="Translate"
        >
          {isTranslating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Globe className="w-4 h-4" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        <DropdownMenuLabel className="text-xs text-gray-500">
          Translate to...
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGE_ORDER.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleTranslate(code)}
            className="cursor-pointer text-sm"
          >
            {LANGUAGES[code]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TranslateButton;
